import React from 'react';
import { Link } from 'react-router-dom';

const About = () => {
  const milestones = [
    {
      year: '2023',
      title: 'Foundation',
      description: 'CodeRidge founded to replace reactive break-fix IT with strategic architecture, security, and operations—the technological ridge organizations climb from.'
    },
    {
      year: '2024',
      title: 'Growth',
      description: 'Expanded delivery across cloud migrations, security hardening, and AI-enabled workflows for teams outgrowing reactive IT.'
    },
    {
      year: '2025',
      title: 'Expansion',
      description: 'Deeper managed operations, hybrid and multi-cloud patterns, and repeat engagement from leaders modernizing core systems.'
    },
    {
      year: '2026',
      title: 'Innovation',
      description: 'Enterprise scale, security, and managed operations for forward-thinking leaders worldwide'
    }
  ];

  const values = [
    {
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
          <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      title: 'Innovation & velocity',
      description: 'We adopt automation, AI, and modern platforms where they remove drag—not for hype. Speed with guardrails is how enterprises actually move.'
    },
    {
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
          <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      title: 'Security & trust',
      description: 'Zero-trust thinking, honest assessments, and operational discipline. We treat your uptime, data, and reputation as seriously as you do.'
    },
    {
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      title: 'Strategic partnership',
      description: 'We operate as an extension of your leadership—clear roadmaps, measurable outcomes, and no black-box IT. Your mission stays in focus.'
    }
  ];

  return (
    <section id="about" className="about">
      <div className="container">
        <div className="section-header">
          <span className="section-badge">About Us</span>
          <h2 className="section-title">Why the market chooses CodeRidge</h2>
          <p className="section-description">
            Speed and security define today&apos;s landscape. We bridge legacy limitations and future-ready performance through intelligent automation, resilient cybersecurity, Cloud 3.0, and managed IT excellence. More than a vendor, we operate as a strategic extension of your leadership—from startups scaling infrastructure to enterprises modernizing security and operations.
          </p>
        </div>

        <div className="milestones-grid">
          {milestones.map((milestone, index) => (
            <div key={index} className="milestone-card">
              <div className="milestone-year">{milestone.year}</div>
              <h3 className="milestone-title">{milestone.title}</h3>
              <p className="milestone-description">{milestone.description}</p>
            </div>
          ))}
        </div>

        <div className="values-grid">
          {values.map((value, index) => (
            <div key={index} className="value-card">
              <div className="value-icon">{value.icon}</div>
              <h3 className="value-title">{value.title}</h3>
              <p className="value-description">{value.description}</p>
            </div>
          ))}
        </div>

        {/* Founder Section */}
        <div className="founder-section">
          <div className="section-header">
            <span className="section-badge">Leadership</span>
            <h2 className="section-title">Driving the next peak</h2>
            <p className="section-description">
              Technology is either your greatest accelerator or your biggest bottleneck. The goal is to make sure it is always the former.
            </p>
          </div>
          <div className="founder-card">
            <div className="founder-image-wrapper">
              <img 
                src="/IMG_6596.JPG" 
                alt="Arun Kumar Vegyarapu - Founder of CodeRidge" 
                className="founder-image"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/400x400/FF6B35/FFFFFF?text=Arun+Kumar+Vegyarapu';
                }}
              />
              <div className="founder-image-overlay"></div>
            </div>
            <div className="founder-content">
              <h3 className="founder-name">Arun Kumar Vegyarapu</h3>
              <p className="founder-title">Founder & CEO, CodeRidge</p>
              <div className="founder-stats">
                <div className="founder-stat">
                  <div className="founder-stat-value">10+</div>
                  <div className="founder-stat-label">Years in Technology</div>
                </div>
                <div className="founder-stat">
                  <div className="founder-stat-value">2023</div>
                  <div className="founder-stat-label">CodeRidge Founded</div>
                </div>
              </div>
              <p className="founder-description">
                Over the last decade in the industry, I have seen how fast the digital landscape shifts—and how strong teams stall when IT stays reactive, only putting out fires instead of fueling growth. I founded CodeRidge to change that.
              </p>
              <p className="founder-description">
                We do not just manage systems. We build the technological ridge: the high-ground infrastructure that lets you scale with clarity and security. We connect complex, evolving technology to real-world outcomes through enterprise AI and automation, Cloud 3.0 architecture, zero-trust cybersecurity, and managed IT excellence—so your people focus on the mission, not the servers.
              </p>
              <p className="founder-description">
                Whether you are a startup designing for your first major scale or an established organization ready for a security and operations overhaul, we bring the expertise to help you reach the next peak. <Link to="/contact">Connect with us</Link> to talk through your stack, or explore <Link to="/portfolio">our work</Link>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
